import React from "react";
import { useParams } from "react-router";
import Box from "@mui/material/Box";
import Tabs from "@mui/material/Tabs";
import Tab from "@mui/material/Tab";
import { useStorageEnabled } from "./StorageEnabledProvider";
import useRoles from "src/hooks/user/useRoles";
import { useProject } from "./ProjectProvider";
import useAuth from "src/hooks/useAuth";
import { usePermissions } from "../../contexts/permissions";

const styles = {
  tabs: { minHeight: 40 },
  tab: {
    minHeight: 40,
    textTransform: "none",
    minWidth: "fit-content",
    flex: 1,
  },
};

export default function ProjectNav({ value, onChange }) {
  const { projectId } = useParams();
  const [storageEnabled] = useStorageEnabled();
  const { project } = useProject();
  const canAccessSettings = useRoles(["admin", "manager", "client", "billing_user"]);
  const canAccessBilling = useRoles(["admin", "manager", "billing_user"]);
  const canAccessSkuLibrary = !useRoles(["employee", "viewer"]);
  const { user } = useAuth();
  const { settings } = user.company;
  const { can } = usePermissions();
  const companyInternalEnabled = settings?.internal_work_orders;

  const outboundEnabled = settings?.inventory_tracking && project?.settings?.display_in_store;
  const skuLibraryEnabled = storageEnabled && canAccessSkuLibrary;
  const groupedItemsEnabled = !!project?.groupedAssets || !!project?.groupedSerializedAssets;
  
  const canAccessSharingTab = can("filter_field2_profile.sharing_tab");
  const canAccessSettingsTab = can("filter_field2_profile.settings_tab");

  const sections = [
    { name: "inbound", label: "Inbound", enabled: storageEnabled },
    { name: "orders", label: "Orders", enabled: outboundEnabled || companyInternalEnabled },
    {
      name: "catalog",
      label: "Catalog",
      enabled: skuLibraryEnabled || !!project?.kitting || groupedItemsEnabled,
    },
    { name: "sharing", label: "Sharing", enabled: canAccessSharingTab },
    { name: "reports", label: "Reports", enabled: true },
    { name: "billing", label: "Billing", enabled: storageEnabled && canAccessBilling },
    { name: "settings", label: "Settings", enabled: projectId && canAccessSettings && canAccessSettingsTab },
  ];

  const list = sections.filter(section => section.enabled);

  const getActiveTab = val => {
    if (["outbound", "internal"].includes(val)) return "orders";
    if (["sku_library", "kitting", "grouped_items"].includes(val)) return "catalog";
    return val;
  };

  const activeTab = getActiveTab(value);

  const resolveTabValue = tab => {
    if (tab === "orders") {
      if (outboundEnabled) return "outbound";
      if (companyInternalEnabled) return "internal";
      return null;
    }

    if (tab === "catalog") {
      if (skuLibraryEnabled) return "sku_library";
      if (project?.kitting) return "kitting";
      if (groupedItemsEnabled) return "grouped_items";
      return null;
    }

    return tab;
  };

  const handleChangeTab = (_event, newValue) => {
    const resolved = resolveTabValue(newValue);
    if (resolved) onChange(resolved);
  };

  return (
    <Box mt={1.5} bgcolor="white">
      <Tabs variant="scrollable" value={activeTab} onChange={handleChangeTab} sx={styles.tabs}>
        {list.map(v => (
          <Tab key={v.name} value={v.name} label={v.label} sx={styles.tab} />
        ))}
      </Tabs>
    </Box>
  );
}
